import { newChipId, normalizeSegments } from './transcriptInlineChipUtils';

function appendTextWithBreaks(el, text) {
  const parts = String(text).split('\n');
  parts.forEach((part, i) => {
    if (part) el.appendChild(document.createTextNode(part));
    if (i < parts.length - 1) el.appendChild(document.createElement('br'));
  });
}

export function createChipElement(name, id, readOnly) {
  const span = document.createElement('span');
  span.className = 'qc-inline-chip';
  span.contentEditable = 'false';
  span.setAttribute('data-chip-id', id);
  span.setAttribute('data-name', name);
  // Keep the chip's own internal layout (label + remove button) left-to-right
  // even inside an RTL transcript, so bidi doesn't reorder them.
  span.setAttribute('dir', 'ltr');

  const label = document.createElement('span');
  label.className = 'qc-inline-chip-label';
  label.textContent = name;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'qc-inline-chip-remove';
  btn.setAttribute('aria-label', `Remove tag ${name}`);
  btn.contentEditable = 'false';
  btn.textContent = '×';
  if (readOnly) {
    btn.disabled = true;
    btn.style.visibility = 'hidden';
    btn.style.pointerEvents = 'none';
  }

  span.appendChild(label);
  span.appendChild(btn);
  return span;
}

export function renderSegmentsIntoElement(el, segments, readOnly) {
  while (el.firstChild) el.removeChild(el.firstChild);
  const list = normalizeSegments(segments);
  for (const s of list) {
    if (s.type === 'text') {
      appendTextWithBreaks(el, s.text ?? '');
    } else {
      el.appendChild(createChipElement(s.name, s.id, readOnly));
    }
  }
  if (el.childNodes.length === 0) {
    el.appendChild(document.createTextNode(''));
  }
}

export function domToSegments(root) {
  const segments = [];
  let textBuf = '';

  function flushText() {
    if (textBuf.length > 0) {
      const last = segments[segments.length - 1];
      if (last?.type === 'text') {
        last.text += textBuf;
      } else {
        segments.push({ type: 'text', text: textBuf });
      }
      textBuf = '';
    }
  }

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      textBuf += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    if (node.classList?.contains('qc-inline-chip')) {
      flushText();
      const name = (node.getAttribute('data-name') ?? '').trim();
      const id = node.getAttribute('data-chip-id') || newChipId();
      if (name) segments.push({ type: 'tag', name, id });
      return;
    }

    if (node.nodeName === 'BR') {
      textBuf += '\n';
      return;
    }

    if (node.nodeName === 'DIV') {
      if (node.childNodes.length === 0) {
        textBuf += '\n';
        return;
      }
      let first = true;
      for (const child of node.childNodes) {
        if (!first) textBuf += '\n';
        first = false;
        walk(child);
      }
      return;
    }

    for (const child of node.childNodes) {
      walk(child);
    }
  }

  for (const child of root.childNodes) {
    walk(child);
  }

  flushText();
  return normalizeSegments(
    segments.length ? segments : [{ type: 'text', text: '' }],
  );
}

export function getRangeInsideEditor(editor) {
  const sel = globalThis.getSelection?.();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.commonAncestorContainer)) return null;
  return range;
}

export function createRangeAtEnd(editor) {
  const range = document.createRange();
  if (editor.childNodes.length === 0) {
    editor.appendChild(document.createTextNode(''));
  }
  range.selectNodeContents(editor);
  range.collapse(false);
  return range;
}

function applyRange(editor, range) {
  const sel = globalThis.getSelection?.();
  sel?.removeAllRanges();
  sel?.addRange(range);
  editor.focus();
}

/**
 * Manual DOM-surgery fallback for browsers where `document.execCommand`
 * isn't available. Cursor lands after the inserted text via a tracked node
 * reference — NOT `frag.lastChild`, which is always null right after
 * `insertNode(frag)` (the fragment's children move into the live DOM,
 * emptying the fragment itself).
 */
function insertPlainTextAtRangeManual(editor, text, range) {
  const r = range.cloneRange();
  r.deleteContents();
  const lines = String(text).split('\n');
  const frag = document.createDocumentFragment();
  let lastNode = null;
  lines.forEach((line, i) => {
    if (line) {
      lastNode = document.createTextNode(line);
      frag.appendChild(lastNode);
    }
    if (i < lines.length - 1) {
      lastNode = document.createElement('br');
      frag.appendChild(lastNode);
    }
  });
  r.insertNode(frag);
  const r2 = document.createRange();
  if (lastNode) {
    r2.setStartAfter(lastNode);
    r2.collapse(true);
  } else {
    r2.setStart(r.startContainer, r.startOffset);
    r2.collapse(true);
  }
  applyRange(editor, r2);
}

export function insertPlainTextAtRange(editor, text, range) {
  if (!editor || !range) return;
  if (!editor.contains(range.commonAncestorContainer)) return;
  // Normalize Windows/old-Mac line endings — otherwise a stray `\r` survives
  // as an invisible character on every line but the last, which then throws
  // off transcript-change-percentage diffing and whatever gets saved.
  const normalized = String(text).replace(/\r\n?/g, '\n');
  // `execCommand('insertText', ...)` (not the manual Range/fragment surgery
  // below) is what makes this paste undoable — the browser only pushes an
  // edit onto its native undo stack when it goes through this command path,
  // not for arbitrary DOM mutation. It also gets cursor placement right on
  // its own. Point the live selection at `range` first since execCommand
  // always operates on the current selection, not an arbitrary Range param.
  const sel = globalThis.getSelection?.();
  if (sel && typeof document.execCommand === 'function') {
    applyRange(editor, range.cloneRange());
    try {
      if (document.execCommand('insertText', false, normalized)) return;
    } catch {
      /* Unsupported in this browser — fall through to the manual path. */
    }
  }
  insertPlainTextAtRangeManual(editor, normalized, range);
}

export function insertPlainTextAtSelection(editor, text) {
  if (!editor) return;
  const range = getRangeInsideEditor(editor) || createRangeAtEnd(editor);
  insertPlainTextAtRange(editor, text, range);
}

export function insertTagAtRange(editor, nameRaw, range) {
  const name = String(nameRaw ?? '').trim();
  if (!name || !editor || !range) return;
  if (!editor.contains(range.commonAncestorContainer)) return;
  const id = newChipId();
  const chip = createChipElement(name, id, false);
  const r = range.cloneRange();
  r.deleteContents();
  r.insertNode(chip);
  const r2 = document.createRange();
  r2.setStartAfter(chip);
  r2.collapse(true);
  applyRange(editor, r2);
}

export function insertTagAtSelection(editor, nameRaw) {
  if (!editor) return;
  const range = getRangeInsideEditor(editor) || createRangeAtEnd(editor);
  insertTagAtRange(editor, nameRaw, range);
}
